import {useQuery,useQueryClient} from "@tanstack/react-query";
import {useAuth} from "@/contexts/AuthContext";
import type {VisitLevel} from "@/lib/visit/address";
import type {VisitResolution} from "@/lib/visit/registry";
import {ApiError} from "@/lib/api/client";
import {useReadMarks} from "./use-read-marks";
import {inboxApi} from "@/lib/api/inbox";
import {announcementsApi} from "@/lib/api/announcements";
import {useNewMessageBox} from "./new-message-box";
import {useConversationReplies} from "./use-conversation-replies";
import {useInboxTransports} from "./inbox-send";
import {conversationsBox} from "./conversations-box";
import {noticesBox} from "./notices-box";
import {inboxRead} from "./inbox-read";
import {inboxWords as words} from "./inbox-copy";

const noFilter={label:words.course,value:"",options:[{value:"",label:words.allCourses}],onChange:()=>{}};
export function useThreadVisit(level:VisitLevel|null):VisitResolution|null {
  const {me}=useAuth();
  const transport=useInboxTransports();
  const read=useQuery({queryKey:["inbox",me?.tenant_id,me?.user_id,"thread",level?.id,level?.job],
    queryFn:()=>inboxApi.thread(level!.id,level!.job!),enabled:Boolean(level?.job&&me),retry:false});
  const existing=read.data?.conversations.length;
  const compose=useNewMessageBox({course:level?.id,recipient:level?.job??"",transport:transport.compose,
    enabled:Boolean(level&&read.data&&!existing)});
  const replies=useConversationReplies(transport.reply,{email:read.data?.channel_modes?.email==="preview",chat:read.data?.channel_modes?.chat==="preview"});
  if(!level)return null;
  if(!level.job || read.error instanceof ApiError && [403,404,422].includes(read.error.status))return {status:"refused"};
  if(!read.data && !read.isError)return {status:"loading"};
  if(!read.data)return {status:"ready",subject:words.conversations,boxes:[conversationsBox({read:inboxRead(read,words.conversationsFailed),actor:me?.user_id??"",query:"",onQueryChange:()=>{},draft:false,task:()=>undefined,course:noFilter,channel:{...noFilter,value:"all"}})],opens:["conversations"]};
  if(!existing)return {status:"ready",subject:read.data.person.name,boxes:[compose.box],opens:["new-message"]};
  return {status:"ready",subject:read.data.person.name,boxes:[conversationsBox({
    read:inboxRead(read,words.conversationsFailed),actor:me?.user_id??"",query:"",onQueryChange:()=>{},draft:false,
    selected:read.data.conversations[0].id,task:replies.task,course:noFilter,
    channel:{...noFilter,label:words.channel,value:"all",options:[{value:"all",label:words.allChannels}]},
  })],opens:["conversations"]};
}

export function useNoticeVisit(level:VisitLevel|null):VisitResolution|null {
  const {me}=useAuth();
  const client=useQueryClient();
  const read=useQuery({queryKey:["announcements",me?.tenant_id,me?.user_id,"notice",level?.id],
    queryFn:()=>announcementsApi.list({notice_id:level!.id}),enabled:Boolean(level&&me),retry:false});
  const item=read.data?.items[0];
  const marks=useReadMarks({items:read.data?.items.map(item=>({id:item.id,unread:!item.is_read})),selected:level?.id,
    write:announcementsApi.markRead,reread:()=>void client.invalidateQueries({queryKey:["announcements"]})});
  if(!level)return null;
  if(read.error instanceof ApiError && [403,404,422].includes(read.error.status) || read.isSuccess&&!item)return {status:"refused"};
  if(!read.data&&!read.isError)return {status:"loading"};
  return {status:"ready",subject:item?.title??words.notices,boxes:[noticesBox({read:inboxRead(read,words.noticesFailed),course:"",onCourseChange:()=>{},selected:level.id,
    mark:marks.mark,query:"",onQueryChange:()=>{}})],opens:["notices"]};
}
